import {
  RichUtils,
  EditorState,
  ContentBlock,
  ContentState,
  getVisibleSelectionRect,
  SelectionState,
  AtomicBlockUtils,
  Entity,
  convertToRaw,
} from 'draft-js';
import { useInput } from 'lib/hooks/use-input';
import { useHasClickedOutside } from 'lib/hooks/use-has-clicked-outside';
import {
  BaseSyntheticEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { constants, getEntityIfSelected } from '../editor-utils';

export const imageStrategy = (
  contentBlock: ContentBlock,
  callback: (start: number, end: number) => void,
  contentState: ContentState
) => {
  contentBlock.findEntityRanges((character) => {
    const entityKey = character.getEntity();
    return (
      entityKey !== null &&
      contentState.getEntity(entityKey).getType() === constants.EntityTypes.LINK
    );
  }, callback);
};

interface Props {
  contentState: ContentState;
  entityKey: string;
  children: any;
}

export const Image = ({ contentState, entityKey, children }: Props) => {
  const { url, alt } = contentState.getEntity(entityKey).getData();

  return (
    <p>
      <img className='' src={url} alt={alt} title={alt} />
    </p>
  );
};

interface ImageComponentProps {
  editorState: EditorState;
  setEditorState: Dispatch<SetStateAction<EditorState>>;
}
export const ImageComponent = ({
  editorState,
  setEditorState,
}: ImageComponentProps) => {
  const [showModal, setShowModal] = useState<boolean>(false);
  const imageUrl = useInput<string>('');
  const imageAlt = useInput<string>('');
  const ref = useRef(null);
  const hasClickedOutside = useHasClickedOutside(ref);
  const alreadyHasImage = getEntityIfSelected(
    constants.BlockTypes.IMAGE,
    editorState
  );

  useEffect(() => {
    if (showModal && hasClickedOutside) {
      setShowModal(false);
      imageUrl.init('');
      imageAlt.init('');
    }
  });

  const handleCreateLink = () => {
    createImage(editorState, setEditorState, imageUrl.value, imageAlt.value);
    setShowModal(false);
    imageUrl.init('');
    imageAlt.init('');
  };

  return (
    <>
      <div className='relative flex'>
        <button
          title='image'
          aria-label='image'
          className='bg-white hover:bg-gray-400 text-gray-800 font-semibold px-2 rounded disabled:text-gray-400'
          onClick={(e) => {
            e.preventDefault();
            setShowModal(true);
          }}
          disabled={!!alreadyHasImage}>
          <svg
            className='w-5 h-8'
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox='0 0 24 24'
            stroke='currentColor'>
            <path
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth={2}
              d='M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z'
            />
          </svg>
        </button>
        {showModal && (
          <div
            ref={ref}
            className='z-10  absolute right-0 mt-8 rounded-md  ring-1 ring-black ring-opacity-5 w-72 [ bg-white border border-gray-100 shadow-lg sm:rounded-lg px-1 py-2 sm:px-2 flex flex-row flex-wrap ] space-y-2 '>
            <label
              htmlFor='src'
              className=' whitespace-nowrap my-auto text-sm font-medium text-gray-700 w-full flex flex-col'>
              Image Src
              <input
                {...imageUrl.bind}
                type='url'
                name='src'
                id='src'
                className='focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-md sm:text-sm border-gray-300 min-w-full'
                placeholder='www.url.com'
                autoComplete='off'
              />
            </label>
            <label
              htmlFor='alt'
              className=' whitespace-nowrap my-auto text-sm font-medium text-gray-700 w-full flex flex-col'>
              Alt Text
              <input
                {...imageAlt.bind}
                type='text'
                name='alt'
                id='alt'
                className='focus:ring-indigo-500 focus:border-indigo-500 block w-full rounded-md sm:text-sm border-gray-300 min-w-full'
                placeholder='What does the image represent'
                autoComplete='off'
              />
            </label>

            <button
              className='-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 disabled:cursor-not-allowed disabled:text-gray-500'
              onClick={handleCreateLink}
              disabled={!imageUrl.isModified || !imageAlt.isModified}>
              <span>Embed</span>
            </button>
          </div>
        )}
      </div>
    </>
  );
};

export const createImage = (
  state: EditorState,
  setState: Dispatch<SetStateAction<EditorState>>,
  imageUrl: string,
  imageAlt: string
) => {
  const contentState = state.getCurrentContent();

  const contentStateWithEntity = contentState.createEntity(
    constants.BlockTypes.IMAGE,
    'IMMUTABLE',
    {
      src: imageUrl,
      alt: imageAlt,
    }
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();

  const newEditorState = AtomicBlockUtils.insertAtomicBlock(
    state,
    entityKey,
    ' '
  );

  setState(newEditorState);
};
interface ImageBlockProps {
  src: string;
  alt: string;
}
export const ImageBlock = (props: ImageBlockProps) => {
  return (
    <p>
      <img
        className='max-h-40'
        src={props.src}
        alt={props.alt}
        title={props.alt}
      />
    </p>
  );
};

export const ImagePlugin = {
  strategy: imageStrategy,
  component: Image,
};
